"use strict";

const { Duration } = require("luxon");

const DEFAULT_PROPS = {
  timezone: "Etc/UTC",
  locale: "en-GB",
  offsetDates: true,
  snapDates: null,
  comparisonRange: null,
  includeTime: true,
  inclusiveEndDate: false
};

const TIME_UNIT_MAP = {
  years: "year",
  months: "month",
  weeks: "week",
  days: "day",
  hours: "hour",
  minutes: "minute",
  seconds: "second",
  milliseconds: "millisecond"
};

const getSanitizedTimeUnit = (value) => TIME_UNIT_MAP[value] || value;

/**
 * Get dateDuration value
 */
const assignDateDurationValue = ({ rollingDateRangeValue }) => {
  if (rollingDateRangeValue) {
    return {
      dateDuration: `P${rollingDateRangeValue}D`
    };
  }
};

/**
 * Assigns a mode, for any branching logic
 */
const assignMode = ({ startDate, endDate, singleDate, dateDuration }) => {
  if (dateDuration) {
    return { mode: "rolling" };
  } else if (startDate && !endDate) {
    return { mode: "since" };
  } else if (singleDate) {
    return { mode: "single" };
  }
  return { mode: "fixed" };
};

/**
 * Get snapDates override
 */
const assignSnapDatesValue = ({
  dateDuration,
  snapDates,
  mode,
  includeTime
}) => {
  if (snapDates) {
    return {
      snapDates: getSanitizedTimeUnit(snapDates)
    };
  }

  // Force snapping to day under certain conditions
  if ((!includeTime && mode !== "rolling") || mode === "single") {
    return {
      snapDates: "day"
    };
  }

  // When there is a dateDuration, set snapDates to the smallest duration-unit specified (e.g. P1Y3M => 'month')
  if (mode === "rolling") {
    const durationObj = Duration.fromISO(dateDuration).toObject();
    const snapUnit = getSanitizedTimeUnit(
      Object.keys(TIME_UNIT_MAP)
        .reverse()
        .find((unit) => durationObj[unit])
    );

    if (snapUnit) {
      return {
        snapDates: snapUnit
      };
    }
  }
};

/**
 * Get includeTime override
 */
const assignIncludeTimeValue = ({ inclusiveEndDate }) => {
  if (inclusiveEndDate) {
    return {
      includeTime: false
    };
  }
};

/**
 * Get timezone override
 * This assigns Etc/UTC when includeTime=false to avoid skewing the dates
 * when the API is not concerned with time
 */
const assignTimezoneValue = ({ includeTime }) => {
  if (!includeTime) {
    return {
      timezone: "Etc/UTC"
    };
  }
};

/**
 * Assigns sensible options based on other input params
 */
const sanitizeProps = (props = {}) => {
  const filteredProps = Object.entries(props)
    .filter(([key, value]) => typeof value !== "undefined")
    .reduce(
      (props, [key, value]) => ({
        ...props,
        [key]: value
      }),
      {}
    );

  return [
    assignDateDurationValue,
    assignMode,
    assignIncludeTimeValue,
    assignTimezoneValue,
    assignSnapDatesValue
  ].reduce((props, fn) => ({ ...props, ...fn(props) }), {
    ...DEFAULT_PROPS,
    ...filteredProps
  });
};

module.exports = sanitizeProps;
